/*
  Отметка об отмене судебного приказа - §3.4.5, §6.4.4.

  Должник вправе подать возражения в десятидневный срок БЕЗ обоснования, и приказ
  отменяется. Это штатная часть процесса, а не сбой, поэтому отметка стоит прямо
  на карточке счёта, рядом с заведением приказа: оба события приходят из суда по
  одному делу.

  До 14.09.2026 отметить отмену было нечем. Стадия «приказ отменён» существовала,
  доля отмен считалась на дашборде, месячный отчёт делал по ней раздел - а попасть
  в стадию можно было только ручной правкой: без даты определения, без выбытия
  приказа из реестра исполнительных документов и без пересчёта стоимости дела.

  Поле причины здесь отсутствует намеренно: возражения обоснования не требуют, и
  «почему» осталось бы пустым почти всегда. Примечание - место для номера
  определения, чтобы дело можно было поднять.
*/

import { useState } from 'react'

import { api } from '../api/client.js'
import { Field, Notice } from './Common.jsx'

/*
  Стадии, из которых отмена возможна. Тот же перечень стоит на сервере
  (`EVENT_SOURCES` в `app/stages/machine.py`) и там же он решающий - здесь он
  только прячет кнопку: предлагать действие, которое заведомо отклонят, значит
  обещать невозможное.

  «Исполнение» в перечне не по недосмотру: возражения подаются со дня получения
  копии приказа, а получить её должник нередко успевает уже от пристава.
*/
const CANCELLABLE_STAGES = ['order_received', 'enforcement']

export default function OrderCancellation({ accountId, stage, districtCourt, onDone }) {
  const [cancelledOn, setCancelledOn] = useState('')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [done, setDone] = useState(null)

  /*
    🔴 Найдено осмотром 14.09.2026. Блок стоит внизу длинной карточки, а общее
    уведомление - наверху: после отметки форма исчезала (стадия сменилась, и
    блок переставал подходить по условию), а сумма доплаты оказывалась на
    полторы тысячи пикселей выше экрана. Человек видел, что форма пропала, и не
    видел главного - во сколько обошлась отмена.

    Поэтому итог показывается ЗДЕСЬ и держит блок на месте, пока человек не
    уйдёт со страницы: условие стадии проверяется только до отметки.
  */
  if (!done && !CANCELLABLE_STAGES.includes(stage)) return null

  async function submit(event) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const answer = await api.cancelOrder({
        account_id: accountId,
        cancelled_on: cancelledOn,
        note: note.trim() || null,
      })
      setCancelledOn('')
      setNote('')
      setDone(answer)
      /*
        Наверх уходит краткое подтверждение: сумма доплаты остаётся здесь, у
        кнопки, которую нажали. §3.4.5 требует предупредить о росте стоимости, а
        предупреждение, до которого надо долистать, предупреждением не является.
      */
      onDone('Приказ отмечен отменённым, счёт переведён в исковое производство.', 'ok')
    } catch (failure) {
      setError(failure)
    } finally {
      setBusy(false)
    }
  }

  if (done) {
    return (
      <section className="section card">
        <h2 className="section__title">Приказ отменён</h2>
        <Notice kind="warn">
          <div className="notice__what">
            Счёт переведён в исковое производство
            {done.documents_cancelled > 0
              ? ', приказ выбыл из реестра исполнительных документов'
              : ''}
            .
          </div>
          <div className="notice__do">{done.cost.explanation}</div>
        </Notice>
        <ClaimAddressee districtCourt={districtCourt} />
      </section>
    )
  }

  return (
    <section className="section card">
      <h2 className="section__title">Приказ отменён</h2>
      <form onSubmit={submit}>
        <div className="row">
          <Field label="Отменён" width="s">
            <input
              className="field__input"
              type="date"
              value={cancelledOn}
              onChange={(event) => setCancelledOn(event.target.value)}
              required
            />
          </Field>
          <Field label="Определение суда" width="m">
            <input
              className="field__input"
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder="номер и дата определения"
            />
          </Field>
          <button className="button" type="submit" disabled={busy || !cancelledOn}>
            Отметить отмену
          </button>
        </div>
      </form>
      {/* Пояснение стоит ПОД строкой с кнопкой: внутри строки оно удлинило бы
          поле снизу и увело кнопку вниз (правило 6г скилла). */}
      <div className="muted">
        Счёт уйдёт в исковое производство, а приказ выбудет из реестра
        исполнительных документов: предъявлять его больше нечего. Пошлина по иску
        вдвое больше приказной, и сумму доплаты вы увидите сразу после отметки.
      </div>
      <ClaimAddressee districtCourt={districtCourt} />
      {error ? <Notice error={error} /> : null}
    </section>
  )
}

function ClaimAddressee({ districtCourt }) {
  /*
    Адресат иска по этому дому - районный суд, указанный у судебного участка
    (14.09.2026). Показывается и до отметки, и после: до - чтобы человек знал,
    определится ли суд; после - потому что именно туда идти дальше.

    Пусто говорится прямо и с местом, где чинить: приказ подаётся мировому
    судье, иск - в районный суд, и тишина здесь читалась бы как «всё готово».
  */
  if (districtCourt) {
    /* Через тире, а не «идёт в:» - наименование суда оператор пишет так, как оно
       стоит в шапке заявления, то есть с предлога: «В Центральный районный
       суд». «Идёт в: В Центральный...» - два предлога подряд (осмотр 14.09.2026). */
    return <div className="muted">Адресат иска по этому дому - {districtCourt}.</div>
  }
  return (
    <div className="muted">
      Районный суд по этому дому не указан - адресата иска придётся вписывать руками.
      Он задаётся у судебного участка: «Настройки» → «Судебные участки».
    </div>
  )
}
